import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useDash } from "@/pages/dashboard/ctx";
import { toast } from "sonner";
import { Layers, Save, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { IndustryPicker } from "@/components/IndustryPicker";
import { SettingsSection } from "@/components/SettingsSection";


const word = (terms, ov, key, which) => (ov?.[key]?.[which] || "").trim() || terms?.[key]?.[which] || "";

// Settings → The Concierge — which industry pack this community runs on, plus per-word overrides.
export function IndustryCard() {
  const { tenantId, role, refreshTenants } = useDash();
  const canEdit = role === "super_admin";
  const [data, setData] = useState(null);
  const [industry, setIndustry] = useState("hoa");
  const [ov, setOv] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!tenantId) return;
    api.get(`/tenants/${tenantId}/industry`).then((r) => {
      setData(r.data); setIndustry(r.data.industry_pack.id); setOv(r.data.overrides || {}); setDirty(false);
    }).catch(() => toast.error("Couldn't load the industry settings"));
  }, [tenantId]);
  useEffect(() => { load(); }, [load]);

  const terms = data?.pack_terms || {};
  const save = async () => {
    setSaving(true);
    try {
      const body = { vocab_overrides: ov };
      if (industry !== data.industry_pack.id) body.industry = industry;
      await api.put(`/tenants/${tenantId}/industry`, body);
      toast.success(industry !== data.industry_pack.id ? "Industry changed — the console relabels now" : "Vocabulary saved");
      load(); refreshTenants?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save"); }
    finally { setSaving(false); }
  };
  const reset = () => { setOv({}); setDirty(true); };

  if (!data) return null;
  const previewOne = (k) => word(terms, ov, k, "one").toLowerCase();
  const previewMany = (k) => word(terms, ov, k, "many").toLowerCase();
  const changingPack = industry !== data.industry_pack.id;
  const nOv = Object.keys(ov).length;

  return (
    <SettingsSection id="industry-card" icon={Layers} title="Industry & vocabulary"
      badges={[{ text: data.industry_pack.name, on: !data.industry_pack.is_default }, nOv > 0 && { text: `${nOv} custom word${nOv === 1 ? "" : "s"}` }]}
      summary={`Which industry this ${previewOne("org")} speaks — the words the console and the concierge use for people, places, decision-makers and modules.`}
      headerRight={canEdit && dirty ? (
        <button type="button" data-testid="industry-save" onClick={save} disabled={saving}
          className="rounded-full bg-ink text-white px-4 py-1.5 text-xs font-semibold hover:bg-ink-700 disabled:opacity-40 inline-flex items-center gap-1.5">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
        </button>
      ) : null}>
      <p className="text-sm text-muted-foreground -mt-1">
        The pack decides what the console and the concierge call the people you serve, the organization, the decision-makers and each module.{!canEdit && " Only Ask Redline changes the pack."}
      </p>

      <div>
        <label className="text-xs font-semibold text-ink/70">Industry pack</label>
        <div className="mt-1.5"><IndustryPicker packs={data.packs || []} value={industry} disabled={!canEdit} onChange={(id) => { setIndustry(id); setDirty(true); }} testid="industry" /></div>
        {changingPack && <p className="text-[11px] text-amber-700 mt-1.5" data-testid="industry-change-note">Changing the pack relabels the whole console and re-briefs the concierge for everyone in this {previewOne("org")}. Stored data is untouched. Press Save to apply.</p>}
      </div>

      <div className="rounded-xl bg-sand-50 border border-sand-200 p-3 text-[13px] leading-relaxed text-ink/80" data-testid="industry-preview">
        <div className="text-[10px] font-bold uppercase tracking-[.12em] text-ink/50 mb-1">Live preview</div>
        A <b>{previewOne("person")}</b> at one of your <b>{previewMany("unit")}</b> opened a <b>{previewOne("request")}</b>; the <b>{previewOne("staff")}</b> answered and <b>{previewOne("board_group")}</b> was copied.
      </div>

      <p className="text-xs text-muted-foreground" data-testid="industry-words-note">
        The words themselves live on the pack, in one place for every {previewMany("org")} that uses it{canEdit ? <> — <Link to="/dashboard/industries" className="font-semibold text-redline-600 hover:text-redline-700" data-testid="industry-edit-pack-link">edit this pack under Industries</Link>.</> : "; Ask Redline edits them."}
        {nOv > 0 && <> This {previewOne("org")} still carries <b>{nOv}</b> older per-{previewOne("org")} word override{nOv === 1 ? "" : "s"}{canEdit && <> — <button type="button" data-testid="vocab-reset" onClick={reset} className="font-semibold underline">clear them</button></>}.</>}
      </p>

      {canEdit && (
        <div className="flex items-center gap-2 justify-end flex-wrap">
          <button type="button" data-testid="industry-save-bottom" onClick={save} disabled={!dirty || saving}
            className="rounded-full bg-ink text-white px-5 py-2 text-sm font-semibold hover:bg-ink-700 disabled:opacity-40 inline-flex items-center gap-1.5">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
          </button>
        </div>
      )}
    </SettingsSection>
  );
}
